import {
  AndroidIcon,
  AppleIcon,
  BatteryCharging,
  BatteryFull,
  BatteryLow,
  BatteryMedium,
  UnknownDeviceIcon
} from '@chain-zen/ui'
import React from 'react'

import type { IDeviceInfo } from '../../../../types/tracking'
import type { getMotionActivity } from '../../../../utils/tracking'

/**
 * A component that displays an icon and a text in a row
 */
export const DeviceInfoRow = ({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) => (
  <div className="flex items-center gap-2">
    {icon}
    <span className="text-foreground">{children}</span>
  </div>
)

const BatteryIcon = ({ batteryLevel, isCharging }: { batteryLevel: number | undefined; isCharging: boolean }) => {
  if (batteryLevel === undefined || batteryLevel < 0) return null

  if (isCharging) {
    return <BatteryCharging className="h-6 w-6" />
  }

  const batteryPercentage = batteryLevel * 100

  if (batteryPercentage >= 80) {
    return <BatteryFull className="h-6 w-6" />
  } else if (batteryPercentage <= 20) {
    return <BatteryLow className="h-6 w-6" />
  } else {
    return <BatteryMedium className="h-6 w-6" />
  }
}

const DevicePlatformIcon = ({ platform }: { platform: string | undefined }) => {
  if (platform === 'ios') {
    return <AppleIcon size={24} />
  } else if (platform === 'android') {
    return <AndroidIcon size={24} />
  } else {
    return <UnknownDeviceIcon size={24} />
  }
}

interface DeviceHeaderProps {
  device: IDeviceInfo | null
}

export const DeviceHeader = ({ device }: DeviceHeaderProps) => (
  <div className="flex w-full justify-between">
    <div className="flex items-center gap-2">
      <DevicePlatformIcon platform={device?.devicePlatform} />
    </div>
    {device?.batteryLevel !== undefined && device?.batteryLevel >= 0 && (
      <div className="flex items-center gap-1">
        <BatteryIcon batteryLevel={device?.batteryLevel} isCharging={device?.isDeviceCharging ?? false} />
        <p className="text-xs">{Math.round(device?.batteryLevel * 100)}%</p>
      </div>
    )}
  </div>
)

interface DeviceInfoSectionProps {
  device: IDeviceInfo | null
  motionActivity: ReturnType<typeof getMotionActivity> | null
  formattedLastSeen: string | null
}

export const DeviceInfoSection = ({ device, motionActivity, formattedLastSeen }: DeviceInfoSectionProps) => (
  <>
    <span className="text-base font-semibold">
      {device?.deviceManufacturer || device?.devicePlatform} {device?.deviceModel} / OS{' '}
      {device?.deviceOSVersion || device?.androidSDKVersion}
    </span>

    {!!device?.languageCode && <span className="text-foreground">Language: {`${device?.languageCode}`}</span>}

    {!!motionActivity && (
      <span className="text-foreground">
        Motion: {motionActivity.emoji} {motionActivity.activity}
      </span>
    )}

    {!!formattedLastSeen && <span className="text-foreground">Last Seen: {formattedLastSeen}</span>}
  </>
)
